import {
  blocksForTime,
  canEditAssignation,
  DAY_BY_ID,
  dayIdForDate,
  intervalForDate,
  isPastOccurrence,
} from "@services/collections/session-assignation";
import type { SessionAssignationRow } from "@services/types/session-assignation";
import type { Section } from "@models/ISchema";
import type {
  SessionAssignationCreation,
  SessionAssignationEditingDraft,
} from "../../types/session-assignation";

export const sectionComponentName = (section: Section): string =>
  (section.component ?? section.courseComponent?.component)?.name ?? section.code ?? section.id;

export const isOccurrenceProtected = (
  row: SessionAssignationRow,
  state: SessionAssignationEditingDraft,
): boolean => {
  const source = state.originalRows.find(original => original.localId === row.localId) ?? row;
  const week = state.weeks.find(item => item.id === source.intervalId);
  return (
    !week || week.isInTerm === false || isPastOccurrence(source, state.policy, week.startingDate)
  );
};

export const canEditOccurrence = (
  row: SessionAssignationRow,
  state: SessionAssignationEditingDraft,
): boolean =>
  state.phase === "editing" &&
  canEditAssignation(state.policy) &&
  row.pendingState !== "deleted" &&
  !isOccurrenceProtected(row, state);

export const rowBlocksCount = (row: SessionAssignationRow): number => {
  if (row.scheduleMode === "scheduled") return row.blocksIds.length;
  const count = Number(row.blocksCountInput);
  return Number.isSafeInteger(count) && count > 0 ? count : 0;
};

export const isOccurrenceDateAllowed = (
  date: string,
  state: SessionAssignationEditingDraft,
  values: SessionAssignationRow | SessionAssignationCreation,
  timeMustMatch = true,
): boolean => {
  try {
    const week = intervalForDate(date, state.weeks);
    const candidate = (startingTime: string | null, scheduleMode: "scheduled" | "unscheduled") =>
      !isPastOccurrence(
        { ...values, date, startingTime, scheduleMode } as SessionAssignationRow,
        state.policy,
        week.startingDate,
      );
    if (values.scheduleMode === "unscheduled") return candidate(null, "unscheduled");
    const catalog = state.blocks.filter(block => block.day === DAY_BY_ID[dayIdForDate(date)]);
    if (!values.scheduleMode)
      return (
        candidate(null, "unscheduled") ||
        catalog.some(block => candidate(block.startingTime, "scheduled"))
      );
    if (
      "dayId" in values &&
      !state.policy.abilities.can_assign_blocks &&
      dayIdForDate(date) !== values.dayId
    )
      return false;
    if (timeMustMatch && values.startingTime && values.endingTime) {
      blocksForTime(date, values.startingTime, values.endingTime, state.blocks);
      return candidate(values.startingTime, "scheduled");
    }
    return catalog.some(block => candidate(block.startingTime, "scheduled"));
  } catch {
    return false;
  }
};

export const emptiedSectionsByDeletion = (
  state: SessionAssignationEditingDraft,
  ids: readonly string[],
): Section[] => {
  const selected = new Set(ids);
  return state.sections.filter(section => {
    const retained = state.rows.filter(
      row => row.sectionId === section.id && row.pendingState !== "deleted",
    );
    return retained.length > 0 && retained.every(row => selected.has(row.localId));
  });
};
